import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, Heart } from 'lucide-react';
import { Slider } from './ui/slider';
import { musicService } from '../services/musicService';
import { authService } from '../services/authService';
import { toast } from 'sonner';

const MusicPlayer = ({ currentSong, onNext, onPrevious, isPlaying, setIsPlaying }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isLiked, setIsLiked] = useState(false);
  const audioRef = useRef(null);

  // Load favorites status
  useEffect(() => {
    const loadFavoriteStatus = async () => {
      if (!currentSong || !authService.isAuthenticated()) {
        setIsLiked(false);
        return;
      }

      try {
        const favorites = await musicService.getFavorites();
        const isSongLiked = favorites.songs.some(s => s.id === currentSong.id);
        setIsLiked(isSongLiked);
      } catch (error) {
        console.error('Error loading favorite status:', error);
      }
    };

    loadFavoriteStatus();
  }, [currentSong]);

  // Handle audio element
  useEffect(() => {
    if (!audioRef.current || !currentSong) return;

    const audio = audioRef.current;
    audio.src = currentSong.audio_url;
    audio.volume = volume / 100;

    if (isPlaying) {
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        toast.error('Error al reproducir la canción');
        setIsPlaying(false);
      });
    } else {
      audio.pause();
    }

    return () => {
      audio.pause();
    };
  }, [currentSong, isPlaying]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(Math.floor(audioRef.current.currentTime));
    }
  };

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(Math.floor(audioRef.current.duration));
    }
  };

  // Handle song end
  const handleEnded = () => {
    onNext();
  };

  // Handle progress change
  const handleProgressChange = (value) => {
    const newTime = value[0];
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  // Toggle favorite
  const toggleFavorite = async () => {
    if (!authService.isAuthenticated()) {
      toast.info('Inicia sesión para agregar favoritos');
      return;
    }

    try {
      if (isLiked) {
        await musicService.removeFromFavorites('songs', currentSong.id);
        setIsLiked(false);
        toast.success('Canción eliminada de favoritos');
      } else {
        await musicService.addToFavorites('songs', currentSong.id);
        setIsLiked(true);
        toast.success('Canción agregada a favoritos');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Error al actualizar favoritos');
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentSong) {
    return null;
  }

  return (
    <>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-40 pb-16 md:pb-0">
      <div className="max-w-screen-2xl mx-auto">
        {/* Mobile Compact Player */}
        <div className="md:hidden px-4 py-3">
          <div className="flex items-center gap-3 mb-2">
            <img
              src={currentSong.cover}
              alt={currentSong.title}
              className="w-12 h-12 rounded-lg object-cover shadow-md"
            />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-gray-900 truncate">{currentSong.title}</h4>
              <p className="text-xs text-gray-500 truncate">{currentSong.artist_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onPrevious}
                className="text-gray-600 hover:text-[#00CCFF] transition-colors duration-200"
              >
                <SkipBack size={20} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] flex items-center justify-center shadow-lg"
              >
                {isPlaying ? (
                  <Pause size={18} className="text-white" fill="white" />
                ) : (
                  <Play size={18} className="text-white ml-0.5" fill="white" />
                )}
              </button>
              <button
                onClick={onNext}
                className="text-gray-600 hover:text-[#00CCFF] transition-colors duration-200"
              >
                <SkipForward size={20} />
              </button>
            </div>
          </div>
          <Slider
            value={[currentTime]}
            max={duration || 1}
            step={1}
            onValueChange={handleProgressChange}
            className="w-full cursor-pointer"
          />
        </div>

        {/* Desktop Full Player */}
        <div className="hidden md:block px-4 py-3">
          {/* Progress Bar */}
          <div className="mb-2">
            <Slider
              value={[currentTime]}
              max={duration || 1}
              step={1}
              onValueChange={handleProgressChange}
              className="w-full cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1 font-medium">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {/* Song Info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <img
                src={currentSong.cover}
                alt={currentSong.title}
                className="w-14 h-14 rounded-xl object-cover shadow-lg transform transition-transform duration-300 hover:scale-110"
              />
              <div className="min-w-0 flex-1">
                <h4 className="text-gray-900 font-bold text-sm truncate">{currentSong.title}</h4>
                <p className="text-gray-500 text-xs truncate">{currentSong.artist_name}</p>
              </div>
              <button
                onClick={toggleFavorite}
                className={`transition-all duration-300 transform hover:scale-110 ${
                  isLiked ? 'text-[#00CCFF]' : 'text-gray-400 hover:text-[#00CCFF]'
                }`}
              >
                <Heart size={20} fill={isLiked ? '#00CCFF' : 'none'} />
              </button>
            </div>

            {/* Player Controls */}
            <div className="flex items-center gap-4 flex-1 justify-center">
              <button
                onClick={onPrevious}
                className="text-gray-600 hover:text-[#00CCFF] transition-all duration-300 transform hover:scale-110"
              >
                <SkipBack size={24} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-12 h-12 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] hover:from-[#056A84] hover:to-[#00CCFF] flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-110"
              >
                {isPlaying ? (
                  <Pause size={22} className="text-white" fill="white" />
                ) : (
                  <Play size={22} className="text-white ml-0.5" fill="white" />
                )}
              </button>
              <button
                onClick={onNext}
                className="text-gray-600 hover:text-[#00CCFF] transition-all duration-300 transform hover:scale-110"
              >
                <SkipForward size={24} />
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              <Volume2 size={20} className="text-gray-500" />
              <Slider
                value={[volume]}
                max={100}
                step={1}
                onValueChange={(value) => setVolume(value[0])}
                className="w-24 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default MusicPlayer;
